import React, { FC, useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem, EuiText, EuiSpacer } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'
import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { useUpdateMarketAnalysisMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import CustomMarkdownEditor from 'components/CustomMarkdownEditor'

export interface EditMarketAnalysisFlyoutProps {
    masterCampaignPlan: MasterCampaignPlan
    onClose: () => void
}

interface MasterCampaignPlanPageParams {
    masterCampaignPlanId: string
}

const EditMarketAnalysisFlyout: FC<EditMarketAnalysisFlyoutProps> = ({ masterCampaignPlan, onClose }) => {
    const { masterCampaignPlanId } = useParams<MasterCampaignPlanPageParams>()
    const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)

    const [marketAnalysisContent, setMarketAnalysisContent] = useState(masterCampaignPlan?.marketAnalysisContent || '')
    const [bodyHeight, setBodyHeight] = useState('calc(100vh - 200px)')

    const headerRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const didFireUpdate = useRef(false)
    const [updateMarketAnalysisMutation, { isLoading: isUpdating, isSuccess }] = useUpdateMarketAnalysisMutation()

    useEffect(() => {
        if (masterCampaignPlan?.marketAnalysisContent) {
            setMarketAnalysisContent(masterCampaignPlan.marketAnalysisContent)
        }
    }, [masterCampaignPlan?.marketAnalysisContent])

    useEffect(() => {
        const calculateBodyHeight = () => {
            const headerHeight = headerRef.current?.clientHeight || 0
            const footerHeight = footerRef.current?.clientHeight || 0
            const totalReservedHeight = headerHeight + footerHeight + 40

            setBodyHeight(`calc(100vh - ${totalReservedHeight}px)`)
        }

        const timer = setTimeout(calculateBodyHeight, 100)

        const handleResize = () => {
            calculateBodyHeight()
        }

        window.addEventListener('resize', handleResize)

        return () => {
            clearTimeout(timer)
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        if (didFireUpdate.current && isSuccess) {
            didFireUpdate.current = false
            onClose()
        }
    }, [didFireUpdate, isSuccess, onClose])

    const onSave = async () => {
        if (!masterCampaignPlanId || !currentOrganization?.id || !currentAccount?.id) {
            return
        }

        const currentContent = marketAnalysisContent

        updateMarketAnalysisMutation({
            masterCampaignPlanId: masterCampaignPlanId,
            marketAnalysisContent: currentContent
        })

        didFireUpdate.current = true
    }

    const isSaveDisabled = marketAnalysisContent.trim().length === 0 || masterCampaignPlan?.marketAnalysisContent === marketAnalysisContent || isUpdating

    const handleEditorChange = (markdown: string) => {
        setMarketAnalysisContent(markdown)
    }

    return (
        <AcFlyout onClose={onClose} style={{ width: '90%' }}>
            <EuiFlyoutHeader>
                <div ref={headerRef}>
                    <EuiTitle size="m">
                        <h2>Edit Market Analysis & Strategy</h2>
                    </EuiTitle>
                    <EuiSpacer size="s" />
                    <EuiText size="s" color="subdued">
                        <p>Edit the market analysis content using the rich markdown editor. Use the toolbar for formatting or markdown shortcuts. Switch between edit and preview modes as needed.</p>
                    </EuiText>
                </div>
            </EuiFlyoutHeader>

            <EuiFlyoutBody style={{
                overflow: 'hidden',
                height: bodyHeight,
                maxHeight: bodyHeight
            }}>
                <CustomMarkdownEditor
                    height={bodyHeight}
                    value={marketAnalysisContent}
                    onChange={handleEditorChange}
                    placeholder="Enter your market analysis content here. You can use Markdown formatting for rich text..."
                    readOnly={isUpdating}
                />
            </EuiFlyoutBody>

            <EuiFlyoutFooter>
                <div ref={footerRef}>
                    <EuiFlexGroup direction="row" alignItems="center">
                        <EuiFlexItem grow={false}>
                            <EuiButton onClick={onClose} isDisabled={isUpdating}>
                                Cancel
                            </EuiButton>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiButton
                                fill
                                onClick={onSave}
                                isLoading={isUpdating}
                                isDisabled={isSaveDisabled}
                            >
                                Save
                            </EuiButton>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiText size="xs">
                                <p>* Content is required</p>
                            </EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </div>
            </EuiFlyoutFooter>
        </AcFlyout>
    )
}

export default EditMarketAnalysisFlyout